import React from 'react';

const OrderSummary = ({ selectedPlan, employeeCount, billingCycle, total }) => {
  const formatCurrency = (amount) => {
    return `K${Math.round(amount)?.toLocaleString()}`;
  };

  const calculateMonthlyPrice = () => {
    if (billingCycle === 'annual') {
      return total / 12;
    }
    return total;
  };

  const calculateDiscount = () => {
    if (billingCycle === 'annual') {
      const monthlyTotal = total / 0.85;
      return monthlyTotal - total;
    }
    return 0;
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-6 sticky top-8">
      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
        Order Summary
      </h3>

      {selectedPlan ? (
        <div className="space-y-4">
          {/* Plan Details */}
          <div className="pb-4 border-b border-gray-200 dark:border-slate-700">
            <div className="flex justify-between items-start mb-2">
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {selectedPlan?.displayName}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {employeeCount} employees
                </p>
              </div>
              <p className="font-semibold text-gray-900 dark:text-white">
                {formatCurrency(calculateMonthlyPrice())}
              </p>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Billed {billingCycle === 'monthly' ? 'monthly' : 'annually'}
            </p>
          </div>

          {/* Pricing Breakdown */}
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Subtotal</span>
              <span className="text-gray-900 dark:text-white">
                {formatCurrency(billingCycle === 'annual' ? total / 0.85 : total)}
              </span>
            </div>

            {billingCycle === 'annual' && (
              <div className="flex justify-between text-sm">
                <span className="text-emerald-600 dark:text-emerald-400">Annual Discount (15%)</span>
                <span className="text-emerald-600 dark:text-emerald-400">
                  -{formatCurrency(calculateDiscount())}
                </span>
              </div>
            )}

            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Tax (GST)</span>
              <span className="text-gray-900 dark:text-white">K0</span>
            </div>
          </div>

          {/* Total */}
          <div className="pt-4 border-t border-gray-200 dark:border-slate-700">
            <div className="flex justify-between items-center">
              <span className="text-lg font-bold text-gray-900 dark:text-white">Total</span>
              <span className="text-2xl font-bold text-emerald-600">
                {formatCurrency(total)}
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {billingCycle === 'monthly' ? 'per month' : 'per year'}
            </p>
          </div>

          {/* Features Summary */}
          <div className="pt-4 border-t border-gray-200 dark:border-slate-700">
            <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
              What's Included:
            </p>
            <ul className="space-y-2">
              {Object?.entries(selectedPlan?.features || {})?.slice(0, 4)?.map(([key, value], idx) => (
                <li key={idx} className="flex items-start text-sm text-gray-600 dark:text-gray-400">
                  <svg className="w-4 h-4 text-emerald-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>{key?.replace(/_/g, ' ')}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Pay-As-You-Go Service */}
          <div className="pt-4 border-t border-gray-200 dark:border-slate-700">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-emerald-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  Pay-As-You-Go Service
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Only pay for what you use. If it doesn't work for you, simply stop using it—no further payments required.
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <svg className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            Select a plan to see pricing details
          </p>
        </div>
      )}
    </div>
  );
};

export default OrderSummary;